<template>
  <div class="gs-asset-type">
    <div class="gs-asset-split">
      <template v-for="(review, index) in blogReviews">
        <section v-if="isBlogReviewTabActive(review)" :key="review.id" class="gs-asset-editor">
          <header class="gs-asset-editor__head">
            <div class="gs-asset-editor__head-fields">
              <gs-field :label="$t('frontend_resources_name')">
                <text-input
                  v-model="review.name"
                  :validator="$v.localFunnelResource.blog_reviews.$each[index].name"
                  :attribute="$t('frontend_groovesell_review_name')"
                  :placeholder="$t('frontend_resources_name')"
                />
              </gs-field>
              <gs-field :label="$t('frontend_resources_title')">
                <text-input
                  v-model="review.title"
                  :validator="$v.localFunnelResource.blog_reviews.$each[index].title"
                  :attribute="$t('frontend_groovesell_review_title')"
                  :placeholder="$t('frontend_resources_title')"
                />
              </gs-field>
            </div>
            <div class="gs-asset-editor__controls">
              <span class="gs-asset-editor__toggle">
                <span>Show to affiliates</span>
                <gd-toggle v-model="review.enabled" :value="review.enabled" />
              </span>
              <button
                type="button"
                class="gs-asset-editor__delete"
                data-action="delete-review"
                title="Delete review"
                aria-label="Delete review"
                @click="openConfirmModal(index)"
              >
                <trash-icon class="w-4 h-4 fill-current" />
              </button>
            </div>
          </header>

          <div class="gs-asset-editor__section">
            <span class="gs-asset-editor__label">{{ $t('frontend_resources_post') }}</span>
            <gs-rich-text-editor
              v-model="review.htmlBody"
              :html-view.sync="isBlogReviewsPageHTMLView"
              :variables="variableMenu"
              :upload-url="uploadUrl"
              :placeholder="$t('frontend_resources_post')"
            />
          </div>

          <div class="gs-asset-editor__section">
            <gs-field label="Plain-text version">
              <div class="gs-asset-editor__plain">
                <textarea
                  v-model="review.textBody"
                  class="gs-asset-editor__textarea"
                  :placeholder="$t('frontend_resources_text_body')"
                ></textarea>
                <gs-name-select
                  placeholder="Insert"
                  max-height="min(280px, 40vh)"
                  :actions="textInsertActions(index)"
                  size="action"
                />
              </div>
            </gs-field>
          </div>
        </section>
      </template>

      <!-- Only shown when there is something to pick: with an empty list the
           list's own empty state is the single message. -->
      <div v-if="hasBlogReviews && !selectedBlogReview" class="gs-asset-empty">
        <p>Select a review from the list to edit it.</p>
      </div>
      <asset-item-list
        title="Blog reviews"
        :items="blogReviews"
        :selected-id="selectedBlogReviewId"
        :add-label="$t('frontend_resources_add_new')"
        name-fallback="Untitled review"
        :empty-copy="$t('frontend_resources_you_currently_donthave_any_blog')"
        enabled-key="enabled"
        :error-ids="blogReviewsErrorIds"
        @select="selectBlogReview"
        @add="createBlogReviews()"
        @reorder="reorderBlogReviews"
      />
    </div>

    <gs-alert-dialog
      v-model="confirmOpen"
      title="Delete this review?"
      :copy="$t('frontend_non_revertable')"
      :confirm-label="$t('frontend_delete_confirm')"
      @confirm="deleteBlogReview()"
    />
  </div>
</template>

<script>
import generateNumberMixin from '@/mixins/generateNumberMixin';
import { required, minLength, maxLength } from 'vuelidate/lib/validators';
import GsRichTextEditor from '@/components/editor/GsRichTextEditor.vue';
import { buildVariableMenu } from '@/components/editor/editorVariables';
import TrashIcon from '@/assets/images/icons/TrashIcon.svg';
import AssetItemList from './AssetItemList.vue';
import { toTabOrder, sortByTabOrder } from './tabOrder';

export default {
  mixins: [generateNumberMixin],
  components: {
    AssetItemList,
    GsRichTextEditor,
    TrashIcon,
  },
  props: {
    funnelResource: {
      type: Object,
      required: true,
    },
    uploadUrl: {
      type: String,
      default: '',
    },
    landingPages: {
      type: Array,
      required: true,
    },
    blogReviewsTab: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      blogReviewId: null,
      confirmOpen: false,
      localFunnelResource: this.funnelResource,
      localBlogReviewsTab: this.blogReviewsTab,
      isBlogReviewsPageHTMLView: false,
    };
  },
  computed: {
    hasBlogReviews() {
      return (
        this.localFunnelResource &&
        this.localFunnelResource.blog_reviews &&
        this.localFunnelResource.blog_reviews.length
      );
    },
    blogReviews() {
      return (this.localFunnelResource && this.localFunnelResource.blog_reviews) || [];
    },
    selectedBlogReview() {
      return this.blogReviews.filter((review) => this.isBlogReviewTabActive(review))[0] || null;
    },
    selectedBlogReviewId() {
      if (!this.localBlogReviewsTab) return null;
      return this.localBlogReviewsTab.replace('blog_review_tab_', '');
    },
    variableMenu() {
      // The Insert menu wears the same two labels the plain-text Insert
      // dropdowns beside it do, so both editors name the tokens identically.
      return buildVariableMenu(this.landingPages, {
        labels: {
          affiliateName: this.$t('frontend_resources_add_affiliate_name'),
          affiliateLink: this.$t('frontend_resources_add_affiliate_link'),
        },
      });
    },
    blogReviewsErrorIds() {
      let blogReviewsErrorIds = [];

      let index;
      for (index in this.$v.localFunnelResource.blog_reviews.$each.$iter) {
        if (this.$v.localFunnelResource.blog_reviews.$each.$iter[index].$error) {
          blogReviewsErrorIds.push(
            this.$v.localFunnelResource.blog_reviews.$each.$iter[index].$model.id
          );
        }
      }

      return blogReviewsErrorIds;
    },
  },
  mounted() {
    this.$root.$on('trigger-validation', () => {
      this.$v.$touch();
    });
  },
  methods: {
    affiliateLinksActionsTextView(index) {
      let affiliateLinksActions = [];

      if (this.landingPages.length > 0) {
        for (let i = 0; i < this.landingPages.length; i++) {
          affiliateLinksActions.push({
            text: this.landingPages[i].description,
            callback: () => this.addAffiliateLinkToReviewsTextView(index, this.landingPages[i].id),
          });
        }
      }

      return affiliateLinksActions;
    },
    textInsertActions(index) {
      return [
        {
          text: this.$t('frontend_resources_add_affiliate_name'),
          callback: () => this.addAffiliateNameToReviewsTextView(index),
        },
        ...this.affiliateLinksActionsTextView(index),
      ];
    },
    updateFunnelResource() {
      this.$emit('updateFunnelResource', this.localFunnelResource);
    },
    selectBlogReview(id) {
      this.handleBlogReviewsTabChange(`blog_review_tab_${id}`);
    },
    // `AssetItemList` reports the new order as ids; the existing handler speaks
    // the tab shape gd-tabs used, so the ids are wrapped back into it.
    reorderBlogReviews(ids) {
      this.handleBlogReviewsTabOrderUpdate(toTabOrder('blog_review', ids));
    },
    handleBlogReviewsTabChange(newTab) {
      this.localBlogReviewsTab = newTab || this.localBlogReviewsTab;
      this.$emit('handleBlogReviewsTabChange', this.localBlogReviewsTab);
    },
    handleBlogReviewsTabOrderUpdate(updatedTabs) {
      sortByTabOrder(this.localFunnelResource.blog_reviews, 'blog_review', updatedTabs);

      this.updateFunnelResource();
    },
    createBlogReviews() {
      const newReviewId = this.generateNumber();
      const newReviewCount = this.localFunnelResource.blog_reviews.length + 1;

      this.localFunnelResource.blog_reviews.push({
        id: newReviewId,
        name: `Review ${newReviewCount}`,
        title: this.$t('frontend_groovesell_tools_affiliate_edit_blog_review_title'),
        htmlBody: '',
        textBody: '',
      });

      this.localBlogReviewsTab = `blog_review_tab_${newReviewId}`;
      this.updateFunnelResource();
      this.handleBlogReviewsTabChange();
    },
    openConfirmModal(blogReviewId) {
      this.blogReviewId = blogReviewId;
      this.confirmOpen = true;
    },
    async deleteBlogReview() {
      this.localFunnelResource.blog_reviews.splice(this.blogReviewId, 1);
      this.localBlogReviewsTab = '';

      if (this.localFunnelResource.blog_reviews.length > 0) {
        let nextOpenIndex = 0;
        if (this.blogReviewId != 0) {
          nextOpenIndex = this.blogReviewId - 1;
        }

        const nextOpenId = this.localFunnelResource.blog_reviews[nextOpenIndex].id;
        this.localBlogReviewsTab = `blog_review_tab_${nextOpenId}`;
      }

      this.updateFunnelResource();
      this.handleBlogReviewsTabChange();
    },
    addAffiliateNameToReviewsTextView(index) {
      this.localFunnelResource.blog_reviews[
        index
      ].textBody = `${this.localFunnelResource.blog_reviews[index].textBody}##affiliateName##`;

      this.updateFunnelResource();
    },
    addAffiliateLinkToReviewsTextView(index, landingPageId) {
      this.localFunnelResource.blog_reviews[
        index
      ].textBody = `${this.localFunnelResource.blog_reviews[index].textBody}##affiliateLink${landingPageId}##`;

      this.updateFunnelResource();
    },
    isBlogReviewTabActive(review) {
      return this.localBlogReviewsTab === `blog_review_tab_${review.id}`;
    },
  },
  validations: {
    localFunnelResource: {
      blog_reviews: {
        $each: {
          name: {
            required,
            minLength: minLength(3),
            maxLength: maxLength(80),
          },
          title: {
            required,
            minLength: minLength(3),
            maxLength: maxLength(80),
          },
          $trackBy: 'id',
        },
      },
    },
  },
};
</script>

<style src="./asset-split.css"></style>
